import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Search, Plus, Edit2, ArrowRightLeft, Warehouse, Package, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';
import { TableSkeleton, CardGridSkeleton } from '@/components/ui/Skeletons';

const RawMaterialInventory = ({ user }) => {
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const userPerms = user?.permissions?.raw_material_inventory || [];
  const canCreate = isAdmin || userPerms.includes('create');
  const canEdit = isAdmin || userPerms.includes('edit');
  const assignedWarehouses = user?.assigned_warehouses || [];
  const [materials, setMaterials] = useState([]);
  const [inventoryByLocation, setInventoryByLocation] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [locationFilter, setLocationFilter] = useState('all');
  const [locations, setLocations] = useState([]);
  const [viewMode, setViewMode] = useState('by-location'); // 'by-location' or 'by-material'
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editTransactionDialogOpen, setEditTransactionDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materialHistory, setMaterialHistory] = useState([]);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [transactionEditForm, setTransactionEditForm] = useState({ quantity: '', location: '', notes: '' });
  const [purchaseForm, setPurchaseForm] = useState({ material_id: '', quantity: '', location: '', notes: '' });
  const [transferForm, setTransferForm] = useState({ material_id: '', quantity: '', from_location: '', to_location: '', notes: '' });

  const handleEditTransaction = (trans) => {
    setEditingTransaction(trans);
    setTransactionEditForm({
      quantity: trans.quantity,
      location: trans.location || '',
      notes: trans.notes || ''
    });
    setEditTransactionDialogOpen(true);
  };

  const submitTransactionEdit = async (e) => {
    e.preventDefault();

    try {
      await axios.put(`${API}/raw-material-transactions/${editingTransaction.id}`, {
        quantity: parseFloat(transactionEditForm.quantity),
        location: transactionEditForm.location,
        notes: transactionEditForm.notes
      });
      toast.success('Transaction updated');
      setEditTransactionDialogOpen(false);
      fetchData();
      if (selectedMaterial) {
        const transRes = await axios.get(`${API}/raw-material-transactions`);
        const history = transRes.data.filter(t => t.material_id === selectedMaterial.id);
        history.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setMaterialHistory(history);
      }
    } catch (error) {
      toast.error('Failed to update transaction');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = inventoryByLocation;

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.material_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.material_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (locationFilter !== 'all' && locationFilter !== '') {
      filtered = filtered.filter(item => item.location === locationFilter);
    }

    setFilteredInventory(filtered);
    setCurrentPage(1);
  }, [searchTerm, locationFilter, inventoryByLocation]);

  const fetchData = async () => {
    try {
      const [matRes, transRes, invByLocRes, locRes] = await Promise.all([
        axios.get(`${API}/raw-materials`),
        axios.get(`${API}/raw-material-transactions`),
        axios.get(`${API}/raw-materials/inventory-by-location`),
        axios.get(`${API}/warehouse-locations`)
      ]);

      setMaterials(matRes.data);
      setTransactions(transRes.data);
      setInventoryByLocation(invByLocRes.data);
      setFilteredInventory(invByLocRes.data);

      const dbLocations = locRes.data.map(l => l.name);
      // Filter locations to user's assigned warehouses for non-admin users
      if (!isAdmin && assignedWarehouses.length > 0) {
        setLocations(dbLocations.filter(l => assignedWarehouses.includes(l)));
      } else {
        setLocations(dbLocations);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/raw-materials/direct-purchase?material_id=${purchaseForm.material_id}&quantity=${purchaseForm.quantity}&location=${purchaseForm.location}&notes=${purchaseForm.notes}`);
      toast.success('Purchase entry added');
      setPurchaseDialogOpen(false);
      setPurchaseForm({ material_id: '', quantity: '', location: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add purchase');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/raw-materials/transfer`, {
        material_id: transferForm.material_id,
        quantity: parseFloat(transferForm.quantity),
        from_location: transferForm.from_location,
        to_location: transferForm.to_location,
        notes: transferForm.notes
      });
      toast.success('Material transferred successfully');
      setTransferDialogOpen(false);
      setTransferForm({ material_id: '', quantity: '', from_location: '', to_location: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer material');
    }
  };

  const openTransferDialog = (item) => {
    setTransferForm({
      material_id: item.material_id,
      quantity: '',
      from_location: item.location || '',
      to_location: '',
      notes: ''
    });
    setTransferDialogOpen(true);
  };

  const showHistory = async (item) => {
    const material = materials.find(m => m.id === item.material_id);
    setSelectedMaterial(material);

    const history = transactions.filter(t => t.material_id === item.material_id);
    history.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setMaterialHistory(history);
    setHistoryDialogOpen(true);
  };

  // Group inventory by location for summary
  const locationSummary = locations.map(loc => {
    const itemsAtLoc = inventoryByLocation.filter(i => i.location === loc);
    const totalItems = itemsAtLoc.length;
    const lowStockItems = itemsAtLoc.filter(i => i.quantity < i.reorder_level).length;
    return { location: loc, totalItems, lowStockItems };
  });

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="h-10 w-48 bg-stone-200/60 rounded animate-pulse mb-2"></div>
        <div className="h-4 w-64 bg-stone-200/60 rounded animate-pulse mb-8"></div>
        <CardGridSkeleton count={4} />
        <div className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm mt-6">
          <TableSkeleton rows={5} columns={7} />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="raw-material-inventory-page">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-4xl font-semibold text-[hsl(145,60%,10%)] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Raw Material Inventory</h1>
          <p className="text-sm text-[hsl(145,20%,40%)] mt-2">Track material stock levels by warehouse location</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
            {canCreate && (
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="transfer-button">
                  <ArrowRightLeft size={16} className="mr-2" />
                  Transfer
                </Button>
              </DialogTrigger>
            )}
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Transfer Material Between Locations</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleTransfer} className="space-y-4">
                <div>
                  <Label>Material</Label>
                  <Select value={transferForm.material_id} onValueChange={(value) => setTransferForm({ ...transferForm, material_id: value })} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select material" />
                    </SelectTrigger>
                    <SelectContent>
                      {materials.map((mat) => (
                        <SelectItem key={mat.id} value={mat.id}>{mat.name} ({mat.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quantity to Transfer</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={transferForm.quantity}
                    onChange={(e) => setTransferForm({ ...transferForm, quantity: e.target.value })}
                    placeholder="Enter quantity"
                    required
                  />
                </div>
                <div>
                  <Label>From Location</Label>
                  <Select value={transferForm.from_location} onValueChange={(value) => setTransferForm({ ...transferForm, from_location: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select source location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                      <SelectItem value="new_location">+ New Location</SelectItem>
                    </SelectContent>
                  </Select>
                  {transferForm.from_location === 'new_location' && (
                    <Input
                      className="mt-2"
                      placeholder="Enter new location name"
                      onChange={(e) => setTransferForm({ ...transferForm, from_location: e.target.value })}
                    />
                  )}
                </div>
                <div>
                  <Label>To Location</Label>
                  <Select value={transferForm.to_location} onValueChange={(value) => setTransferForm({ ...transferForm, to_location: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select destination location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                      <SelectItem value="new_location">+ New Location</SelectItem>
                    </SelectContent>
                  </Select>
                  {transferForm.to_location === 'new_location' && (
                    <Input
                      className="mt-2"
                      placeholder="Enter new location name"
                      onChange={(e) => setTransferForm({ ...transferForm, to_location: e.target.value })}
                    />
                  )}
                </div>
                <div>
                  <Label>Notes (Optional)</Label>
                  <Input
                    value={transferForm.notes}
                    onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                    placeholder="Transfer reason"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                    <ArrowRightLeft size={14} className="mr-2" />
                    Transfer
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
            {canCreate && (
              <DialogTrigger asChild>
                <Button className="bg-green-600 hover:bg-green-700 text-white" data-testid="add-purchase-button">
                  <Plus size={16} className="mr-2" />
                  Add Purchase
                </Button>
              </DialogTrigger>
            )}
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Raw Material Purchase</DialogTitle>
              </DialogHeader>
              <form onSubmit={handlePurchase} className="space-y-4">
                <div>
                  <Label>Material</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between font-normal"
                      >
                        {purchaseForm.material_id
                          ? materials.find(m => m.id === purchaseForm.material_id)?.name + ` (${materials.find(m => m.id === purchaseForm.material_id)?.code})`
                          : "Select material"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search material..." />
                        <CommandList>
                          <CommandEmpty>No material found.</CommandEmpty>
                          <CommandGroup>
                            {materials.map((mat) => (
                              <CommandItem
                                key={mat.id}
                                value={mat.name + " " + mat.code}
                                onSelect={() => {
                                  setPurchaseForm({ ...purchaseForm, material_id: mat.id });
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${purchaseForm.material_id === mat.id ? 'opacity-100' : 'opacity-0'}`} />
                                {mat.name} ({mat.code})
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input type="number" step="0.01" value={purchaseForm.quantity} onChange={(e) => setPurchaseForm({ ...purchaseForm, quantity: e.target.value })} required />
                </div>
                <div>
                  <Label>Location / Warehouse</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between font-normal"
                      >
                        {purchaseForm.location
                          ? purchaseForm.location === 'new_location' ? '+ New Location' : purchaseForm.location
                          : "Select location"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search location..." />
                        <CommandList>
                          <CommandEmpty>No location found.</CommandEmpty>
                          <CommandGroup>
                            {locations.map((loc) => (
                              <CommandItem
                                key={loc}
                                value={loc}
                                onSelect={() => {
                                  setPurchaseForm({ ...purchaseForm, location: loc });
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${purchaseForm.location === loc ? 'opacity-100' : 'opacity-0'}`} />
                                {loc}
                              </CommandItem>
                            ))}
                            <CommandItem
                              value="new_location"
                              onSelect={() => {
                                setPurchaseForm({ ...purchaseForm, location: 'new_location' });
                              }}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              New Location
                            </CommandItem>
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {purchaseForm.location === 'new_location' && (
                    <Input
                      className="mt-2"
                      placeholder="Enter new location name"
                      onChange={(e) => setPurchaseForm({ ...purchaseForm, location: e.target.value })}
                    />
                  )}
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input value={purchaseForm.notes} onChange={(e) => setPurchaseForm({ ...purchaseForm, notes: e.target.value })} placeholder="Purchase details" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setPurchaseDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white">Add Purchase</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Warehouse filter info banner for non-admin users */}
      {user?.role !== 'admin' && user?.assigned_warehouses?.length > 0 && (
        <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-sm flex items-center gap-2">
          <Warehouse size={16} className="text-blue-600 shrink-0" />
          <span className="text-sm text-blue-700">
            Showing inventory for your assigned warehouses: <strong>{user.assigned_warehouses.join(', ')}</strong>
          </span>
        </div>
      )}

      {
        locationSummary.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {locationSummary.map((loc) => (
              <div
                key={loc.location}
                className={`p-4 rounded-sm border cursor-pointer transition-all ${locationFilter === loc.location ? 'border-[hsl(145,60%,20%)] bg-[hsl(145,60%,20%)]/5' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                onClick={() => setLocationFilter(locationFilter === loc.location ? 'all' : loc.location)}
                data-testid={`location-card-${loc.location}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Warehouse size={18} className="text-[hsl(145,60%,20%)]" />
                  <span className="font-medium text-[hsl(145,60%,10%)]">{loc.location}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[hsl(145,20%,40%)]">{loc.totalItems} items</span>
                  {loc.lowStockItems > 0 && (
                    <span className="text-red-600 font-medium">{loc.lowStockItems} low</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      }

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(145,20%,40%)]" size={18} />
          <Input
            placeholder="Search by material code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-inventory"
          />
        </div>
        <Select value={locationFilter} onValueChange={setLocationFilter} data-testid="filter-location">
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Locations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map((loc) => (
              <SelectItem key={loc} value={loc}>{loc}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Inventory Table - By Location */}
      <div className="bg-white rounded-sm border border-stone-200 shadow-sm overflow-hidden mb-4">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full" data-testid="inventory-table">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-[hsl(145,20%,40%)] uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[hsl(145,20%,40%)] uppercase tracking-wider">Material Name</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[hsl(145,20%,40%)] uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Warehouse size={14} />
                    Location
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[hsl(145,20%,40%)] uppercase tracking-wider">Location Stock</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[hsl(145,20%,40%)] uppercase tracking-wider">Global Current Stock</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[hsl(145,20%,40%)] uppercase tracking-wider">Unit</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[hsl(145,20%,40%)] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {filteredInventory.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((item, idx) => (
                <tr
                  key={`${item.material_id}-${item.location}-${idx}`}
                  className="hover:bg-stone-50/50 transition-colors"
                  data-testid={`inventory-row-${item.material_code}-${item.location}`}
                >
                  <td className="px-6 py-3 text-sm text-[hsl(145,60%,10%)] font-medium">{item.material_code}</td>
                  <td className="px-6 py-3 text-sm text-[hsl(145,60%,10%)]">{item.material_name}</td>
                  <td className="px-6 py-3 text-sm">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                      {item.location}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-sm">
                    <span className={`font-medium ${item.quantity < item.reorder_level ? 'text-red-600' : 'text-[hsl(145,60%,10%)]'}`}>
                      {item.quantity.toLocaleString()}
                    </span>
                    {item.quantity < item.reorder_level && (
                      <span className="ml-2 text-xs text-red-500">(Low Stock)</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm text-[hsl(145,60%,10%)]">
                    {(() => {
                      const mat = materials.find(m => m.id === item.material_id);
                      return mat ? mat.current_stock.toLocaleString() : '-';
                    })()}
                  </td>
                  <td className="px-6 py-3 text-sm text-[hsl(145,20%,40%)]">{item.unit}</td>
                  <td className="px-6 py-3 text-sm">
                    <div className="flex gap-1">
                      {canCreate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openTransferDialog(item)}
                          title="Transfer to another location"
                          data-testid={`transfer-${item.material_code}`}
                        >
                          <ArrowRightLeft size={14} />
                        </Button>
                      )}
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => showHistory(item)}
                          data-testid={`view-history-${item.material_code}`}
                        >
                          View History
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredInventory.length === 0 && (
            <div className="p-8 text-center text-[hsl(145,20%,40%)]" data-testid="no-inventory">
              No inventory found for selected criteria
            </div>
          )}
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden flex flex-col divide-y divide-stone-100">
          {filteredInventory.length === 0 ? (
            <div className="p-8 text-center text-sm text-[hsl(145,20%,40%)]">
              No inventory found for selected criteria
            </div>
          ) : (
            filteredInventory.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((item, idx) => (
              <div key={`mobile-${item.material_id}-${item.location}-${idx}`} className="p-4 bg-white">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold text-[hsl(145,60%,10%)]">{item.material_name}</h4>
                    <p className="text-xs text-[hsl(145,20%,40%)]">{item.material_code}</p>
                  </div>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium whitespace-nowrap">
                    {item.location}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                  <div className="bg-stone-50 p-2 rounded">
                    <span className="text-xs text-stone-500 block mb-1">Loc Stock</span>
                    <span className={`font-semibold ${item.quantity < item.reorder_level ? 'text-red-600' : 'text-stone-900'}`}>
                      {item.quantity.toLocaleString()} {item.unit}
                    </span>
                    {item.quantity < item.reorder_level && <span className="text-[10px] text-red-500 block">Low Stock</span>}
                  </div>
                  <div className="bg-stone-50 p-2 rounded">
                    <span className="text-xs text-stone-500 block mb-1">Glob Stock</span>
                    <span className="font-semibold text-stone-900">
                      {(() => {
                        const mat = materials.find(m => m.id === item.material_id);
                        return mat ? mat.current_stock.toLocaleString() : '-';
                      })()} {item.unit}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 justify-end mt-2 pt-3 border-t border-stone-100">
                  {canCreate && (
                    <Button variant="outline" size="sm" className="h-8 text-xs flex-1" onClick={() => openTransferDialog(item)}>
                      <ArrowRightLeft size={12} className="mr-1.5" /> Transfer
                    </Button>
                  )}
                  {canEdit && (
                    <Button variant="outline" size="sm" className="h-8 text-xs flex-1" onClick={() => showHistory(item)}>
                      History
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <div className="flex justify-center">
        <Pagination currentPage={currentPage} totalItems={filteredInventory.length} onPageChange={setCurrentPage} />
      </div>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Material History: {selectedMaterial?.name} ({selectedMaterial?.code})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 p-4 bg-stone-50 rounded-sm">
              <div>
                <p className="text-xs text-[hsl(145,20%,40%)] uppercase tracking-wider font-bold">Current Stock</p>
                <p className="text-2xl font-semibold text-[hsl(145,60%,20%)]">{selectedMaterial?.current_stock} {selectedMaterial?.unit}</p>
              </div>
              <div>
                <p className="text-xs text-[hsl(145,20%,40%)] uppercase tracking-wider font-bold">Reorder Level</p>
                <p className="text-xl font-semibold text-[hsl(145,60%,10%)]">{selectedMaterial?.reorder_level} {selectedMaterial?.unit}</p>
              </div>
              <div>
                <p className="text-xs text-[hsl(145,20%,40%)] uppercase tracking-wider font-bold">MOQ</p>
                <p className="text-xl font-semibold text-[hsl(145,60%,10%)]">{selectedMaterial?.moq} {selectedMaterial?.unit}</p>
              </div>
            </div>

            <div className="border border-stone-200 rounded-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-bold text-stone-600 uppercase">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-stone-600 uppercase">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-stone-600 uppercase">Current Stock</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-stone-600 uppercase">Location</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-stone-600 uppercase">Reference</th>
                    {canEdit && <th className="px-4 py-2 text-left text-xs font-bold text-stone-600 uppercase">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {materialHistory.map((trans) => (
                    <tr key={trans.id} className="hover:bg-stone-50/50">
                      <td className="px-4 py-2 text-sm text-[hsl(145,60%,10%)]">
                        {new Date(trans.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${trans.transaction_type === 'IN' || trans.transaction_type === 'TRANSFER_IN' || trans.transaction_type === 'Purchase'
                          ? 'bg-green-100 text-green-700'
                          : trans.transaction_type === 'OUT' && trans.reference_type?.includes('PRODUCTION')
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-red-100 text-red-700'
                          }`}>
                          {trans.transaction_type === 'OUT' && trans.reference_type?.includes('PRODUCTION') ? 'Production Consumption' : trans.transaction_type}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-[hsl(145,60%,10%)] font-medium">
                        {trans.transaction_type === 'IN' || trans.transaction_type === 'TRANSFER_IN' || trans.transaction_type === 'Purchase' ? '+' : '-'}{trans.quantity}
                      </td>
                      <td className="px-4 py-2 text-sm text-[hsl(145,20%,40%)]">{trans.location || 'N/A'}</td>
                      <td className="px-4 py-2 text-sm text-[hsl(145,20%,40%)]">
                        {trans.reference_type?.includes('PRODUCTION') && !trans.notes?.includes('SO:')
                          ? `Consumed for Flow Ref: ${trans.reference_id}`
                          : trans.notes || trans.reference_type || '-'}
                      </td>
                      {canEdit && (
                        <td className="px-4 py-2 text-sm">
                          <Button variant="ghost" size="sm" onClick={() => handleEditTransaction(trans)}>
                            <Edit2 size={12} />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Dialog */}
      <Dialog open={editTransactionDialogOpen} onOpenChange={setEditTransactionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Transaction</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitTransactionEdit} className="space-y-4">
            <div>
              <Label>Current Stock</Label>
              <Input
                type="number"
                step="0.01"
                value={transactionEditForm.quantity}
                onChange={(e) => setTransactionEditForm({ ...transactionEditForm, quantity: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Location</Label>
              <Select value={transactionEditForm.location} onValueChange={(value) => setTransactionEditForm({ ...transactionEditForm, location: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={transactionEditForm.notes}
                onChange={(e) => setTransactionEditForm({ ...transactionEditForm, notes: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditTransactionDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div >
  );
};

export default RawMaterialInventory;
